"use client";

import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Stack,
  Group,
  Badge,
  Box,
  Button,
  Divider,
  Image,
  Grid,
} from "@mantine/core";
import { IconChevronRight, IconHanger } from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { trackCtaClick } from "@/lib/analytics";
import { kibbeTypes } from "@/app/[locale]/kibbe/kibbeData";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";

// Group types by family for organized display
const kibbeByFamily = {
  dramatic: ["dramatic", "soft-dramatic"],
  natural: ["flamboyant-natural", "natural", "soft-natural"],
  classic: ["dramatic-classic", "classic", "soft-classic"],
  gamine: ["flamboyant-gamine", "gamine", "soft-gamine"],
  romantic: ["theatrical-romantic", "romantic"],
};

const familyColors: Record<string, string> = {
  dramatic: "gray",
  natural: "teal",
  classic: "blue",
  gamine: "violet",
  romantic: "pink",
};

export default function KibbeOutfitsContent({ locale }: { locale?: string }) {
  const t = useTranslations("kibbeOutfits");

  const familyDescriptions: Record<string, string> = {
    dramatic: t("dramaticDescription"),
    natural: t("naturalDescription"),
    classic: t("classicDescription"),
    gamine: t("gamineDescription"),
    romantic: t("romanticDescription"),
  };

  return (
    <Container size="xl" py={48}>
      {/* Editorial masthead */}
      <Box mb={40}>
        <Divider color="dark.9" size={2} mb={20} />
        <Text fz={11} tt="uppercase" c="dimmed" style={{ letterSpacing: 3 }} mb={20}>
          {t("styleGuideLabel")}
        </Text>
        <Title order={1} fz={{ base: 32, md: 52 }} fw={300} lh={1.05} style={{ letterSpacing: -2 }} mb={16}>
          {t("pageTitle")}
        </Title>
        <Text fz={{ base: "md", md: "lg" }} c="dark.5" maw={640} lh={1.65}>
          {t("pageSubtitle")}
        </Text>
        <Group gap="xs" wrap="wrap" mt={20}>
          {Object.keys(kibbeByFamily).map((family) => (
            <Button
              key={family}
              component="a"
              href={`#${family}`}
              variant="outline"
              color={familyColors[family] === "gray" ? "dark" : familyColors[family]}
              size="xs"
              radius="xl"
            >
              {t("typesButton", { family: family.charAt(0).toUpperCase() + family.slice(1) })}
            </Button>
          ))}
        </Group>
        <Divider color="gray.3" mt={24} />
      </Box>

      {/* Not Sure CTA */}
      <Box p={{ base: 20, md: 28 }} mb={48} style={{ background: "#faf7f2", borderTop: "3px solid #141414", borderRadius: "var(--mantine-radius-lg)" }}>
        <Group justify="space-between" align="center" gap="lg" wrap="wrap">
          <Box maw={560}>
            <Text fw={600} mb={4}>
              {t("notSureTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("notSureDescription")}
            </Text>
          </Box>
          <Button
            component={Link}
            href="/kibbe-body-type-test"
            onClick={() => trackCtaClick("kibbe_outfits_hub_test")}
            color="violet"
            rightSection={<IconChevronRight size={16} />}
          >
            {t("takeTestButton")}
          </Button>
        </Group>
      </Box>

      {/* Types by Family — label rail left, uniform card grid right */}
      <Stack gap={56}>
        {Object.entries(kibbeByFamily).map(([family, typeIds]) => (
          <Box key={family} id={family}>
            <Grid gutter={{ base: 16, md: 32 }}>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <Box
                  pt={4}
                  style={{ borderTop: `2px solid var(--mantine-color-${familyColors[family]}-5)` }}
                >
                  <Text fz={11} tt="uppercase" c="dimmed" style={{ letterSpacing: 2 }} mt={12} mb={6}>
                    {t("typesCount", { count: typeIds.length })}
                  </Text>
                  <Title order={2} fz={24} fw={400} tt="capitalize" mb={8} style={{ letterSpacing: -0.5 }}>
                    {t("familyTitle", { family })}
                  </Title>
                  <Text size="sm" c="dimmed" lh={1.6}>
                    {familyDescriptions[family]}
                  </Text>
                </Box>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 9 }}>
                <SimpleGrid cols={{ base: 2, sm: 3 }} spacing={{ base: 12, md: 20 }}>
                  {typeIds.map((typeId) => {
                    const typeData = kibbeTypes[typeId];
                    if (!typeData) return null;

                    return (
                      <Card
                        key={typeId}
                        padding={0}
                        radius="lg"
                        withBorder
                        className="hub-card"
                        style={{ overflow: "hidden", transition: "transform 0.2s, box-shadow 0.2s" }}
                      >
                        <Box
                          component={Link}
                          href={`/kibbe-outfits/${typeId}`}
                          style={{ position: "relative", display: "block", aspectRatio: "3/4" }}
                        >
                          <Image
                            src={`/kibbe-outfits/${typeId}/outfit-1.webp?v3`}
                            alt={`${typeData.name} outfit ideas`}
                            h="100%"
                            w="100%"
                            style={{ position: "absolute", inset: 0, objectFit: "cover" }}
                            fallbackSrc={`/kibbe/outfits/${typeId}.png`}
                          />
                          <Box
                            style={{
                              position: "absolute",
                              inset: 0,
                              background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.66) 100%)",
                            }}
                          />
                          <Box style={{ position: "absolute", bottom: 12, left: 14, right: 14 }}>
                            <Text fw={600} fz="lg" c="white" lh={1.2} style={{ letterSpacing: -0.3 }}>
                              {typeData.name}
                            </Text>
                            <Text fz="xs" c="rgba(255,255,255,0.85)" lineClamp={1}>
                              {typeData.tagline}
                            </Text>
                          </Box>
                        </Box>
                        <Box p="sm">
                          <Button
                            component={Link}
                            href={`/kibbe-outfits/${typeId}`}
                            variant="default"
                            size="xs"
                            fullWidth
                            rightSection={<IconHanger size={14} />}
                          >
                            {t("viewOutfitsButton", { typeName: typeData.name })}
                          </Button>
                        </Box>
                      </Card>
                    );
                  })}
                </SimpleGrid>
              </Grid.Col>
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* General Kibbe Outfit Tips — editorial numbered columns */}
      <Box mt={64} mb={48}>
        <Divider color="dark.9" size={2} mb={20} />
        <Title order={2} fz={24} fw={400} mb={28} style={{ letterSpacing: -0.5 }}>
          {t("universalTipsTitle")}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing={{ base: 20, md: 28 }}>
          {(
            [
              [t("honorLinesTitle"), t("honorLinesDescription")],
              [t("silhouetteTrendTitle"), t("silhouetteTrendDescription")],
              [t("fabricMattersTitle"), t("fabricMattersDescription")],
              [t("scaleDetailsTitle"), t("scaleDetailsDescription")],
            ] as [string, string][]
          ).map(([tipTitle, tipDescription], i) => (
            <Box key={tipTitle} pt={12} style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}>
              <Text fz={26} fw={200} c="gray.4" lh={1} mb={8} style={{ letterSpacing: -1 }}>
                {String(i + 1).padStart(2, "0")}
              </Text>
              <Text fw={600} fz="sm" mb={4}>
                {tipTitle}
              </Text>
              <Text size="sm" c="dimmed" lh={1.6}>
                {tipDescription}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* CTA */}
      <Box ta="center" py="xl">
        <Title order={2} fz={24} fw={400} mb="sm" style={{ letterSpacing: -0.5 }}>
          {t("ctaTitle")}
        </Title>
        <Text c="dimmed" mb="xl" maw={500} mx="auto">
          {t("ctaDescription")}
        </Text>
        <QuizPhotoUpload quizName="Kibbe Outfits Hub" />
      </Box>

      <style jsx global>{`
        .hub-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }
      `}</style>
    </Container>
  );
}
